import React, { useState } from "react";
import { Field, ErrorMessage, useFormikContext } from "formik";
import {
  Box,
  Typography,
  Chip,
  Checkbox,
  Autocomplete,
  InputBase,
  IconButton,
  Button,
} from "@mui/material";
import {
  CheckBox,
  CheckBoxOutlineBlank,
  ExpandMore,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import "./service-accordion.scss";
import "./create-services.scss";

interface Props {
  packageKey: "Basic" | "Standard" | "Premium";
  step: number;
  title: string;
  serviceCategories: any[];
  mode?: "create" | "edit" | "view";
  isPackageEditing?: boolean;
  originalPackage: {
    isActive: boolean;
    price: number | null;
    description: string | null;
    services: number[];
    id?: number | null;
  };
  onEdit?: () => void;
  onSave?: (key: "Basic" | "Standard" | "Premium") => void;
  onCancel?: () => void;
  isSaving: boolean;
}

interface PackageData {
  isActive: boolean;
  price: number | null;
  description: string;
  services: number[];
}

interface FormValues {
  packages: {
    Basic: PackageData;
    Standard: PackageData;
    Premium: PackageData;
  };
}

const ServicePackageAccordion: React.FC<Props> = ({
  packageKey,
  step,
  title,
  serviceCategories,
  mode = "create",
  isPackageEditing = false,
  originalPackage,
  onEdit,
  onCancel,
  onSave,
  isSaving,
}) => {
  const { values, setFieldValue, validateForm, setFieldTouched, errors } =
    useFormikContext<FormValues>();
  const packageData = values.packages[packageKey];

  const isEnabled = packageData.isActive;
  const isCreateMode = mode === "create";
  const isEditMode = mode === "edit" && isPackageEditing;
  const isViewMode = mode === "view";

  const [isOpen, setIsOpen] = useState(true);

  const selectedServices = serviceCategories.filter((c) =>
    packageData.services?.includes(c.id),
  );
  const canEditServices = (isCreateMode || isEditMode) && isEnabled;
  const currentPkg = values.packages[packageKey];
  const hasChanges =
    JSON.stringify(originalPackage) !== JSON.stringify(currentPkg);
  const hasErrors = Boolean(errors?.packages?.[packageKey]);
  const handleToggle = () => {
    setFieldValue(`packages.${packageKey}.isActive`, !packageData.isActive);
  };
  const touchPackageFields = async (key: "Basic" | "Standard" | "Premium") => {
    await Promise.all([
      setFieldTouched(`packages.${key}.price`, true, false),
      setFieldTouched(`packages.${key}.description`, true, false),
      setFieldTouched(`packages.${key}.services`, true, false),
    ]);
  };
  const validatePackage = async (key: "Basic" | "Standard" | "Premium") => {
    await touchPackageFields(key);

    const formErrors = await validateForm();

    return Boolean(
      formErrors?.packages?.[key]?.price ||
        formErrors?.packages?.[key]?.description ||
        formErrors?.packages?.[key]?.services,
    );
  };

  if (isViewMode) {
    return (
      <Box className="package-card view-mode">
        <Box className="package-header view-header">
          <Box className="left">
            <span className="step">{step}</span>
            <Typography className="services-offerings__title">
              {title}
            </Typography>
            <span
              className={`status-badge ${isEnabled ? "active" : "inactive"}`}
            >
              {isEnabled ? "Active" : "Inactive"}
            </span>
          </Box>

          {mode !== "create" && (
            <IconButton size="small" onClick={onEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box className="view-body">
          <Box className="package-info-row">
            <Box className="info-block price">
              <Typography className="label">Price ($)</Typography>
              <Typography className="value price-value">
                {packageData.price != null ? `$${packageData.price}` : "-"}
              </Typography>
            </Box>
            <Box className="info-spacer" />

            <Box className="info-block description">
              <Typography className="label">Description</Typography>
              <Typography
                variant="body2"
                component="p"
                className="value description-text"
                style={{ wordBreak: "break-word" }}
              >
                {packageData.description || "-"}
              </Typography>
            </Box>
          </Box>

          <Box className="divider" />

          <Box>
            <Typography className="label description-text" style={{ fontSize: "14px"}}>
          SERVICES INCLUDED ({selectedServices.length})
          </Typography>

          <Box className="services-list">
            {selectedServices.map((s) => (
              <span key={s.id} className="service-chip">
                {s.label}
              </span>
            ))}
          </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="services-offerings package-card">
      <Box
        className="package-header"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Box className="left">
          <span className="step">{step}</span>
          <Typography className="services-offerings__title">{title}</Typography>

          <label className="toggle" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={handleToggle}
            />
            <span className="slider" />
          </label>
        </Box>

        <ExpandMore className={`chevron ${isOpen ? "open" : ""}`} />
      </Box>

      {isOpen && (
        <Box className="accordion-body expanded">
          <Box className="package-body">
            <Box className="form-group">
              <Typography className="services-offerings__subcategories-text">
                Price ($) <span className="required">*</span>
              </Typography>
              <Field name={`packages.${packageKey}.price`}>
                {({ field }) => (
                  <input
                    {...field}
                    type="number"
                    value={field.value ?? ""}
                    disabled={!isEnabled}
                    className="form-control"
                    placeholder="$ Enter package price"
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) =>
                      e.target instanceof HTMLElement && e.target.blur()
                    }
                  />
                )}
              </Field>
              <ErrorMessage
                name={`packages.${packageKey}.price`}
                component="div"
                className="error"
              />
            </Box>

            <Box className="form-group">
              <Typography className="services-offerings__subcategories-text">
                Description <span className="required">*</span>
              </Typography>
              <Field name={`packages.${packageKey}.description`}>
                {({ field }) => {
                  const maxLength = 500;
                  const currentLength = field.value ? field.value.length : 0;

                  return (
                    <>
                      <textarea
                        {...field}
                        value={field.value ?? ""}
                        rows={4}
                        maxLength={maxLength}
                        disabled={!isEnabled}
                        className="form-control"
                        placeholder="Provide a brief description of what this package offers..."
                        style={{ marginBottom: "14px" }}
                      />

                      <div
                        style={{
                          fontSize: "13px",
                          color: currentLength > maxLength ? "red" : "#666",
                          marginBottom: "6px",
                        }}
                      >
                        {currentLength}/{maxLength} characters
                      </div>
                    </>
                  );
                }}
              </Field>
              <ErrorMessage
                name={`packages.${packageKey}.description`}
                component="div"
                className="error"
              />
            </Box>

            <Box className="form-group">
              <Typography className="services-offerings__title">
                Services Included <span className="required">*</span>
              </Typography>
              <Typography
                variant="body2"
                className="services-offerings__subtitle"
                sx={{ mt: 1 }}
              >
                Add services that will be included in this package
              </Typography>

              <Autocomplete
                multiple
                disableCloseOnSelect
                disabled={!isEnabled}
                options={serviceCategories}
                getOptionLabel={(option) => option.label}
                value={selectedServices}
                onChange={(e, newValue) => {
                  setFieldValue(
                    `packages.${packageKey}.services`,
                    newValue.map((c) => c.id),
                  );
                  setFieldTouched(
                    `packages.${packageKey}.services`,
                    true,
                    false,
                  );
                }}
                renderTags={() => null}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                      sx={{
                        mr: 1,
                        p: "4px",
                        borderRadius: "6px",
                        color: "#9ca3af",
                        "&.Mui-checked": {
                          color: "#15585d",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(21, 88, 93, 0.08)",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: "18px",
                          borderRadius: "4px",
                        },
                      }}
                    />
                    {option.label}
                  </li>
                )}
                sx={{
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",                  "& .MuiAutocomplete-inputRoot": {
                    padding: 0,
                  },
                }}
                renderInput={(params) => (
                  <InputBase
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder="Select services..."
                    sx={{
                      width: "100%",
                      minHeight: "32px",
                    }}
                  />
                )}
              />
              <ErrorMessage
                name={`packages.${packageKey}.services`}
                component="div"
                className="error"
                style={{ marginTop: "10px" }}
              />

              {selectedServices.length > 0 && (
                <Box className="services-offerings__chips">
                  {selectedServices.map((option) => (
                    <Chip
                      key={option.id}
                      label={option.label}
                      {...(canEditServices && {
                        deleteIcon: <span>✕</span>,
                        onDelete: () => {
                          const updatedServices = packageData.services.filter(
                            (id) => id !== option.id,
                          );

                          setFieldValue(
                            `packages.${packageKey}.services`,
                            updatedServices,
                          );

                          if (updatedServices.length === 0) {
                            setFieldTouched(
                              `packages.${packageKey}.services`,
                              true,
                              false,
                            );
                          }
                        },
                      })}
                      className="services-offerings__chip"
                    />
                  ))}
                </Box>
              )}
            </Box>
            {mode === "edit" && isPackageEditing && (
              <Box
                className="action-buttons buttons-wrapper"
                mt={3}
                display="flex"
                gap={2}
                justifyContent="flex-end"
              >
                <Button
                  className="cancel-btn"
                  variant="outlined"
                  color="secondary"
                  onClick={onCancel}
                >
                  Cancel
                </Button>

                <Button
                  className="save-btn"
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    const errors = await validatePackage(packageKey);
                    if (!errors) {
                      onSave?.(packageKey);
                    }
                  }}
                  disabled={!hasChanges || hasErrors}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ServicePackageAccordion;
